// BilledTogetherPanel — read-only billing rollup (Phase 3, 2026-07-17).
// Shows, per payer/anchor group: member lines, rate tiers, mgmt fee %,
// master total, and who pays. Sourced entirely from service_assignments via
// computeBillingRollup — this panel has no write path at all.
//
// 2026-10-19 (item 552, Ben's pick on the demo): the heading says "Billed
// together" only when an invoice really covers two or more facilities; every
// line names its facility (also ones in other buildings that share the group —
// they used to show as bare ids) and its cleaner; and a "Building total
// (running lines)" closes the box — the members' own Active lines only.

import type { BuildingOverview } from '../types';

const money = (n: number) =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

/** "Modoc (Deborah Crimble)" — a facility named with its cleaner, the house rule. */
export function lineLabel(
  facilityName: string,
  cleaner: string | null | undefined,
): string {
  return cleaner ? `${facilityName} (${cleaner})` : facilityName;
}

export function BilledTogetherPanel({ overview }: { overview: BuildingOverview }) {
  const nameFor = (facilityId: string | null) =>
    (facilityId && overview.facility_names[facilityId]) || facilityId || '—';
  const cleanerFor = (providerId: string | null) =>
    (providerId && overview.provider_names[providerId]) || null;

  return (
    <div>
      {overview.has_shared_invoice && (
        <h4 className="text-[10px] font-semibold uppercase tracking-[0.08em] text-tcs-text-muted">
          Billed together
        </h4>
      )}
      <div className={`${overview.has_shared_invoice ? 'mt-2 ' : ''}space-y-3`}>
        {overview.rollup_groups.map((g) => (
          <div
            key={`${g.anchor_facility_id}-${g.payer_label}`}
            className="rounded-lg border border-tcs-border bg-tcs-surface p-3"
          >
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <p className="text-xs text-tcs-text-secondary">
                Pays: <span className="font-medium text-tcs-text">{g.payer_label}</span>
                <span className="text-tcs-text-muted"> · anchored at {nameFor(g.anchor_facility_id)}</span>
                {g.spans_buildings && (
                  <span className="ml-1 rounded bg-violet-500/10 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-violet-600">
                    spans locations
                  </span>
                )}
              </p>
              <p className="font-mono text-sm font-semibold text-tcs-text">{money(g.monthly_total)}/mo</p>
            </div>

            <ul className="mt-2 divide-y divide-tcs-border/60">
              {g.lines.map((line) => (
                <li key={line.assignment_id} className="flex items-center justify-between gap-2 py-1.5">
                  <span className="min-w-0 truncate text-[11px] text-tcs-text-secondary">
                    {lineLabel(nameFor(line.facility_id), cleanerFor(line.provider_id))}
                    {line.billing_method ? ` · ${line.billing_method.replace(/_/g, ' ')}` : ''}
                  </span>
                  <span className="shrink-0 font-mono text-[11px] text-tcs-text">
                    {line.billing_rate != null ? money(Number(line.billing_rate)) : '—'}
                  </span>
                </li>
              ))}
            </ul>

            <p className="mt-2 text-[10px] text-tcs-text-muted">
              {g.distinct_rates.length > 1 && `${g.distinct_rates.length} rate tiers`}
              {g.distinct_rates.length > 1 && g.management_fee_pcts.length > 0 && ' · '}
              {g.management_fee_pcts.length > 0 &&
                `mgmt fee ${g.management_fee_pcts.map((f) => `${(f * 100).toFixed(1).replace(/\.0$/, '')}%`).join(', ')}`}
            </p>
          </div>
        ))}
      </div>
      <div className="mt-3 flex items-baseline justify-between gap-2 border-t border-tcs-border pt-2">
        <p className="text-xs font-medium text-tcs-text">Building total (running lines)</p>
        <p className="font-mono text-sm font-semibold text-tcs-text">{money(overview.building_total)}/mo</p>
      </div>
      <p className="mt-2 text-[10px] italic text-tcs-text-muted">
        Read-only — view choices never change billing. Billing edits go through Finance proposals.
      </p>
    </div>
  );
}
